import { create } from 'zustand'
import type { Mode } from '@/theme/tokens'
import i18n from '@/i18n'
import { isRegisteredLocale, type RegisteredLocaleCode } from '@/i18n/localeRegistry'
import type { ChapterGenerateLang, ChapterGenerateMode } from '@/api/chapterApi'

export type Locale = RegisteredLocaleCode

/** 创作繁简档位。全局共享: 任一处切换, 所有文字工作页面同步生效。 */
export type ContentMode = 'easy' | 'advanced'

export interface GeneratingChapter {
  projectId: string
  branchId: string
  chapterNumber: number
  outline: string
  title?: string
  chapterMode?: ChapterGenerateMode
  targetWords?: number
  lang?: ChapterGenerateLang
}

interface UiState {
  mode: Mode
  contentMode: ContentMode
  locale: Locale
  currentBranchId: string
  selectedChapterId: string | null
  selectedChapterProjectId: string | null
  selectedChapterBranchId: string | null
  generatingChapter: GeneratingChapter | null
  isGenerating: boolean
  generatingAbort: (() => void) | null
  toggleMode: () => void
  setMode: (mode: Mode) => void
  setContentMode: (mode: ContentMode) => void
  setLocale: (l: Locale) => void
  setCurrentBranchId: (branchId: string) => void
  selectChapter: (input: { projectId: string; branchId: string; chapterId: string }) => void
  requestSelectChapter: (
    input: { projectId: string; branchId: string; chapterId: string },
    confirmSwitchAway: () => boolean,
  ) => boolean
  clearSelectedChapter: () => void
  openChapterGenerateForm: (input: GeneratingChapter) => boolean
  startGeneratingChapter: (input: GeneratingChapter) => boolean
  finishGeneratingChapter: () => void
  clearGeneratingChapter: () => void
  setGeneratingAbort: (abort: (() => void) | null) => void
}

const LS_MODE = 'nf.mode'
const LS_CONTENT_MODE = 'nf.content_mode'
const LS_LOCALE = 'nf.locale'
const LS_BRANCH = 'nf.branch_id'
export const DEFAULT_BRANCH_ID = 'main'

function initMode(): Mode {
  if (typeof localStorage === 'undefined') return 'dark'
  const saved = localStorage.getItem(LS_MODE)
  return saved === 'light' || saved === 'dark' ? saved : 'dark'
}
function initContentMode(): ContentMode {
  if (typeof localStorage === 'undefined') return 'easy'
  return localStorage.getItem(LS_CONTENT_MODE) === 'advanced' ? 'advanced' : 'easy'
}
function initLocale(): Locale {
  if (typeof localStorage === 'undefined') return 'en'
  const saved = localStorage.getItem(LS_LOCALE)
  if (isRegisteredLocale(saved)) return saved
  const browserLanguage = typeof navigator !== 'undefined' && typeof navigator.language === 'string' ? navigator.language : ''
  const browserPrefix = browserLanguage.split('-')[0]
  return isRegisteredLocale(browserPrefix) ? browserPrefix : 'en'
}
function initBranchId(): string {
  if (typeof localStorage === 'undefined') return DEFAULT_BRANCH_ID
  return localStorage.getItem(LS_BRANCH)?.trim() || DEFAULT_BRANCH_ID
}

function applyMode(mode: Mode) {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  root.dataset.theme = mode
  root.classList.toggle('dark', mode === 'dark')
}

export const useUiStore = create<UiState>((set, get) => {
  applyMode(initMode())
  return {
    mode: initMode(),
    contentMode: initContentMode(),
    locale: initLocale(),
    currentBranchId: initBranchId(),
    selectedChapterId: null,
    selectedChapterProjectId: null,
    selectedChapterBranchId: null,
    generatingChapter: null,
    isGenerating: false,
    generatingAbort: null,
    toggleMode: () => {
      const next: Mode = get().mode === 'dark' ? 'light' : 'dark'
      if (typeof localStorage !== 'undefined') localStorage.setItem(LS_MODE, next)
      applyMode(next)
      set({ mode: next })
    },
    setMode: (mode) => {
      if (typeof localStorage !== 'undefined') localStorage.setItem(LS_MODE, mode)
      applyMode(mode)
      set({ mode })
    },
    setContentMode: (contentMode) => {
      if (typeof localStorage !== 'undefined') localStorage.setItem(LS_CONTENT_MODE, contentMode)
      set({ contentMode })
    },
    setLocale: (l) => {
      if (typeof localStorage !== 'undefined') localStorage.setItem(LS_LOCALE, l)
      set({ locale: l })
      if (i18n.language !== l) void i18n.changeLanguage(l)
    },
    setCurrentBranchId: (branchId) => {
      const next = branchId.trim() || DEFAULT_BRANCH_ID
      if (typeof localStorage !== 'undefined') localStorage.setItem(LS_BRANCH, next)
      set({
        currentBranchId: next,
        selectedChapterId: null,
        selectedChapterProjectId: null,
        selectedChapterBranchId: null,
      })
    },
    selectChapter: ({ projectId, branchId, chapterId }) => {
      set({
        selectedChapterId: chapterId,
        selectedChapterProjectId: projectId,
        selectedChapterBranchId: branchId,
      })
    },
    requestSelectChapter: (input, confirmSwitchAway) => {
      const state = get()
      const scopedGeneratingChapter =
        state.generatingChapter?.projectId === input.projectId && state.generatingChapter.branchId === input.branchId
          ? state.generatingChapter
          : null
      if (scopedGeneratingChapter && state.isGenerating) {
        if (!confirmSwitchAway()) return false
        state.generatingAbort?.()
        get().clearGeneratingChapter()
      } else if (scopedGeneratingChapter) {
        get().clearGeneratingChapter()
      }
      get().selectChapter(input)
      return true
    },
    clearSelectedChapter: () => {
      set({
        selectedChapterId: null,
        selectedChapterProjectId: null,
        selectedChapterBranchId: null,
      })
    },
    openChapterGenerateForm: (input) => {
      if (get().isGenerating) return false
      set({
        generatingChapter: input,
        isGenerating: false,
      })
      return true
    },
    startGeneratingChapter: (input) => {
      if (get().isGenerating) return false
      set({
        generatingChapter: input,
        isGenerating: true,
      })
      return true
    },
    finishGeneratingChapter: () => {
      set({ isGenerating: false, generatingAbort: null })
    },
    clearGeneratingChapter: () => {
      set({ generatingChapter: null, isGenerating: false, generatingAbort: null })
    },
    setGeneratingAbort: (abort) => {
      set({ generatingAbort: abort })
    },
  }
})
